<template>
  <div class="message-input flex w-full gap-2">
    <textarea
      v-model="messageContent"
      placeholder="Napišite poruku..."
      class="flex-grow bg-gray-800 text-white border-2 border-gray-600 rounded-lg px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm sm:text-base"
      @keydown.enter.prevent="handleEnterKey"
      :disabled="isSending"
      rows="1"
      ref="textareaRef"
      style="min-height: 44px; max-height: 120px"
    ></textarea>
    <button
      @click="sendMessage"
      class="bg-blue-600 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center min-w-12 sm:min-w-20 border-2 border-blue-600 flex-shrink-0"
      :disabled="!canSend || isSending"
    >
      <svg
        v-if="!isSending"
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="sm:w-6 sm:h-6"
      >
        <line x1="22" y1="2" x2="11" y2="13"></line>
        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
      </svg>
      <svg
        v-else
        class="animate-spin h-5 w-5 sm:h-6 sm:w-6"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </button>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  watch,
  nextTick,
} from 'vue';

export default defineComponent({
  name: 'MessageInput',
  emits: ['send', 'error'],
  setup(props, { emit }) {
    const messageContent = ref('');
    const isSending = ref(false);
    const textareaRef = ref<HTMLTextAreaElement | null>(null);

    const canSend = computed(() => messageContent.value.trim().length > 0);

    // Auto-resize textarea based on content
    const adjustTextareaHeight = () => {
      const textarea = textareaRef.value;
      if (!textarea) return;

      // Reset height to default to correctly calculate scroll height
      textarea.style.height = '44px';

      // Set new height based on scroll height (capped at max-height via CSS)
      const newHeight = Math.min(textarea.scrollHeight + 8, 120); // Add padding
      textarea.style.height = `${newHeight}px`;
    };

    // Watch for content changes to adjust height
    watch(messageContent, () => {
      nextTick(adjustTextareaHeight);
    });

    // Initialize height adjustment
    onMounted(() => {
      adjustTextareaHeight();
    });

    const handleEnterKey = (event: KeyboardEvent) => {
      // If shift+enter, insert a newline
      if (event.shiftKey) {
        const start = textareaRef.value?.selectionStart || 0;
        const end = textareaRef.value?.selectionEnd || 0;

        messageContent.value =
          messageContent.value.substring(0, start) +
          '\n' +
          messageContent.value.substring(end);

        // Set cursor position after the inserted newline
        nextTick(() => {
          if (textareaRef.value) {
            textareaRef.value.selectionStart = start + 1;
            textareaRef.value.selectionEnd = start + 1;
            adjustTextareaHeight();
          }
        });
      } else {
        // If just enter, send the message
        sendMessage();
      }
    };

    const sendMessage = async () => {
      if (canSend.value && !isSending.value) {
        const content = messageContent.value;
        messageContent.value = '';
        isSending.value = true;

        try {
          await emit('send', content);
          // Reset textarea height after sending
          nextTick(adjustTextareaHeight);
        } catch (error) {
          // If sending failed, return the message to the input field
          messageContent.value = content;
          emit('error', error);
        } finally {
          isSending.value = false;
        }
      }
    };

    return {
      messageContent,
      canSend,
      isSending,
      sendMessage,
      textareaRef,
      handleEnterKey,
    };
  },
});
</script>

<style scoped>
/* Ensure the textarea focus doesn't get cut off */
textarea:focus {
  border-width: 2px !important;
  outline: none !important;
}

/* Mobile optimizations */
@media (max-width: 640px) {
  textarea {
    font-size: 16px; /* Prevents zoom on iOS */
  }
}
</style>
